import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CoverageMentionSchema } from '@/services/types/coverage-types';

import { EnrichmentDetails } from './EnrichmentDetails';
import { ExtractionDetails } from './ExtractionDetails';
import { IntegrationDetails } from './IntegrationDetails';
import { MentionOverview } from './MentionOverview';
import { RawDataView } from './RawDataView';
import { ValidationDetails } from './ValidationDetails';

interface MentionDetailsProps {
  mention: CoverageMentionSchema;
  onBack: () => void;
}

export const MentionDetails = ({ mention, onBack }: MentionDetailsProps) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <div>
          <h2 className="text-xl font-semibold">Mention Details</h2>
          <p className="text-sm text-gray-600">
            ID: {mention.id} • Artifact: {mention.artifact_type} (
            {mention.artifact_id.slice(-8)})
          </p>
        </div>
      </div>

      {/* Overview Card */}
      <MentionOverview mention={mention} />

      {/* Detailed Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Processing Pipeline Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="extraction" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="extraction">Extraction</TabsTrigger>
              <TabsTrigger value="validation">Validation</TabsTrigger>
              <TabsTrigger value="enrichment">Enrichment</TabsTrigger>
              <TabsTrigger value="integration">Integration</TabsTrigger>
              <TabsTrigger value="raw">Raw Data</TabsTrigger>
              <TabsTrigger value="feedback">Feedback</TabsTrigger>
            </TabsList>

            <div className="mt-6">
              <TabsContent value="extraction">
                <ExtractionDetails extraction={mention.extraction} />
              </TabsContent>

              <TabsContent value="validation">
                <ValidationDetails validation={mention.validation} />
              </TabsContent>

              <TabsContent value="enrichment">
                <EnrichmentDetails
                  searchQueries={mention.search_queries}
                  searchResults={mention.search_results}
                  candidate={mention.candidate}
                />
              </TabsContent>

              <TabsContent value="integration">
                <IntegrationDetails
                  existingEntities={mention.existing_entities}
                  integration={mention.integration}
                  patch={mention.patch}
                />
              </TabsContent>

              <TabsContent value="raw">
                <RawDataView mention={mention} />
              </TabsContent>

              <TabsContent value="feedback">
                <div className="space-y-4">
                  {mention.feedback &&
                  Object.keys(mention.feedback).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(mention.feedback).map(([key, value]) => (
                        <div key={key} className="border rounded-lg p-4">
                          <h4 className="font-medium text-sm text-gray-700 mb-2">
                            {key}
                          </h4>
                          <p className="text-sm">{value}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No feedback available for this mention</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
